// Every point value in one place so the rubric is easy to tune.
export const GRADE_WEIGHTS = {
    base: 60, // a valid submission (business name + a line + a contact) starts here (D-)
    baseInvalid: 30, // missing business name or lines → unworkable → F range
    docs: { acord: 13, lossruns: 6, decpage: 2, supplemental: 1, financials: 1 },
    profile: {
        physicalAddress: 3, website: 1, fein: 1, yearsInBusiness: 2,
        sicNaics: 1, grossAnnualRevenue: 2, annualPayroll: 1, knownLosses: 2,
    },
    narrative: { descriptionDetailed: 2, executiveSummary: 1, quoteNeedBy: 1, incumbentCarrier: 1 },
    descriptionWordThreshold: 20, // words before a description counts as "detailed"
};
/**
 * The floor for sending a submission out — B-, i.e. 83.
 *
 * ⚠️ A GATE, NOT A HINT (Josh, 2026-08-30). A submission below this cannot be
 * sent to AAS or emailed to markets. The reasoning is that a thin submission
 * doesn't fail politely: it gets ignored by an underwriter, and the agent
 * concludes the market has no appetite when really the packet was unworkable.
 *
 * Enforced on the SERVER for anything that leaves through us. The market-email
 * path builds a file in the browser and cannot be enforced there — a determined
 * member can always attach a PDF to their own email, which is fine. The gate is
 * meant to stop careless sends, not to make sending impossible.
 */
export const MIN_SEND_SCORE = 83;
// A value counts as "present" when it's a non-empty string that isn't a bare "0"
// (the wizard stores empty numerics as "0" / "").
function present(v) {
    if (typeof v === 'number')
        return v !== 0;
    const s = String(v ?? '').trim();
    return s !== '' && s !== '0';
}
/**
 * Score to letter.
 *
 * ⚠️ EXPORTED SO THERE IS ONE OF THESE. The Submission Portal shows an average
 * grade across a member's submissions, and the obvious way to build that card
 * was a second cutoff table in the page — which is how the two phone formatters
 * that disagreed happened. A member seeing "B" on the card and "B-" on every row
 * beneath it would be right to distrust both.
 */
export function letterFor(s) {
    if (s < 60)
        return 'F';
    if (s < 63)
        return 'D-';
    if (s < 67)
        return 'D';
    if (s < 70)
        return 'D+';
    if (s < 73)
        return 'C-';
    if (s < 77)
        return 'C';
    if (s < 80)
        return 'C+';
    if (s < 83)
        return 'B-';
    if (s < 87)
        return 'B';
    if (s < 90)
        return 'B+';
    if (s < 93)
        return 'A-';
    if (s < 97)
        return 'A';
    return 'A+';
}
export function gradeSubmission(data) {
    const W = GRADE_WEIGHTS;
    const boosts = [];
    const gaps = [];
    const validName = present(data.businessName);
    const validLines = Array.isArray(data.lines) && data.lines.length > 0;
    const validContact = present(data.submitterEmail) || present(data.email);
    const valid = validName && validLines && validContact;
    let score = valid ? W.base : W.baseInvalid;
    // ---- Documents (heaviest) ----
    const docs = (Array.isArray(data.documents) ? data.documents : []).filter((d) => (d.storagePath && String(d.storagePath).trim() !== '') || d.attached === true);
    const kinds = new Set(docs.map((d) => String(d.kind)));
    if (kinds.has('acord')) {
        score += W.docs.acord;
        boosts.push('ACORDs attached');
    }
    else
        gaps.push('No ACORD forms');
    if (kinds.has('lossruns')) {
        score += W.docs.lossruns;
        boosts.push('Loss runs attached');
    }
    else
        gaps.push('No loss runs');
    if (kinds.has('decpage')) {
        score += W.docs.decpage;
        boosts.push('Dec pages');
    }
    else
        gaps.push('No dec pages');
    if (kinds.has('supplemental'))
        score += W.docs.supplemental;
    if (kinds.has('financials'))
        score += W.docs.financials;
    // ---- Account profile ----
    if (present(data.physicalAddress))
        score += W.profile.physicalAddress;
    else
        gaps.push('No physical address');
    if (present(data.website))
        score += W.profile.website;
    else
        gaps.push('No website');
    if (present(data.fein))
        score += W.profile.fein;
    if (present(data.yearsInBusiness))
        score += W.profile.yearsInBusiness;
    else
        gaps.push('No years in business');
    if (present(data.sicNaics))
        score += W.profile.sicNaics;
    if (present(data.grossAnnualRevenue))
        score += W.profile.grossAnnualRevenue;
    else
        gaps.push('No revenue');
    if (present(data.annualPayroll))
        score += W.profile.annualPayroll;
    if (present(data.knownLosses))
        score += W.profile.knownLosses;
    // ---- Narrative / context ----
    const words = String(data.descriptionOfOps ?? '').trim().split(/\s+/).filter(Boolean).length;
    if (words >= W.descriptionWordThreshold) {
        score += W.narrative.descriptionDetailed;
        boosts.push('Detailed operations');
    }
    else
        gaps.push('Thin description of operations');
    if (present(data.executiveSummary))
        score += W.narrative.executiveSummary;
    if (present(data.quoteNeedBy))
        score += W.narrative.quoteNeedBy;
    if (present(data.incumbentCarrier))
        score += W.narrative.incumbentCarrier;
    if (!valid) {
        if (!validName)
            gaps.unshift('No business name');
        if (!validLines)
            gaps.unshift('No lines of business');
        if (!validContact)
            gaps.unshift('No contact email');
    }
    score = Math.max(0, Math.min(100, Math.round(score)));
    const grade = letterFor(score);
    return { score, grade, band: grade[0].toLowerCase(), boosts, gaps };
}
